import React, { useEffect } from "react";

import { Button, Title } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import { setPageTitle } from "@/utils/UPageTitle";

export interface IErrorLayoutProps {
	title?: string;
	text?: string;
	pageTitle?: string;
	/** The page the "back" button goes to; none hides the button. */
	backTo?: string;
	children?: React.ReactNode;
}

/**
 * An error page: a title and a line, a way home. The router's 404 and
 * error element, and what a page shows when its resource is not found.
 */
export const ErrorLayout: React.FC<IErrorLayoutProps> = ({
	title,
	pageTitle,
	text,
	backTo = "/",
	children,
}) => {
	const navigate = useNavigate();
	const shownTitle = title ?? t("errors.title");
	const shownText = text ?? t("errors.generic");

	useEffect(() => {
		setPageTitle(pageTitle ?? shownTitle);
	}, [shownTitle, pageTitle]);

	if (children) return <div>{children}</div>;

	return (
		<div className="flex h-full w-full flex-col justify-between p-8">
			<div className="h-1/2">
				<Title size="h1">{shownTitle}</Title>
				<p className="mt-4">{shownText}</p>
				{backTo && (
					<Button
						className="mt-6"
						variant="outline"
						picto="arrow"
						onClick={() => navigate(backTo)}
					>
						{t("errors.backHome")}
					</Button>
				)}
			</div>

			<div className="right-0 bottom-0 mt-auto ml-auto h-1/2 pt-10">
				<img
					src={"/assets/illustrations/searching.svg"}
					alt="Searching"
					className="ml-auto h-full"
				/>
			</div>
		</div>
	);
};

/** The catch-all route: an address that matches nothing. */
export const NotFoundLayout: React.FC = () => (
	<ErrorLayout title={t("errors.notFound")} text={t("errors.pageNotFound")} />
);
